import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Auth = () => {
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showResetForm, setShowResetForm] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [showSetupButton, setShowSetupButton] = useState(false);
  const [checkingSuperAdmin, setCheckingSuperAdmin] = useState(true);
  
  const navigate = useNavigate();
  const { isAuthenticated, signIn, resetPassword } = useAuth();
  const { toast } = useToast();

  // Check if super admin exists
  useEffect(() => {
    const checkSuperAdminExists = async () => {
      try {
        const { data, error } = await supabase.rpc('super_admin_exists');
        if (error) {
          console.error('Error checking super admin:', error);
          setShowSetupButton(true); // Show on error to be safe
        } else {
          setShowSetupButton(!data); // Show setup button only if super admin doesn't exist
        }
      } catch (error) {
        console.error('Error checking super admin:', error);
        setShowSetupButton(true); // Show on error to be safe
      } finally {
        setCheckingSuperAdmin(false);
      }
    };

    checkSuperAdminExists();
  }, []);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const clearMessages = () => {
    setError("");
    setSuccessMessage("");
  };

  const showError = (message: string) => {
    setError(message);
    setSuccessMessage("");
    
    // Check if rate limited
    if (message.includes("⏰") || message.toLowerCase().includes("wait")) {
      setIsRateLimited(true);
      // Clear rate limit after 5 minutes
      setTimeout(() => setIsRateLimited(false), 5 * 60 * 1000);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setError("");
    setIsRateLimited(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);

    try {
      const result = await signIn(loginEmail, loginPassword);
      
      if (result.success) {
        showSuccess("✅ Welcome back! Redirecting...");
        toast({
          title: "Welcome back!",
          description: "You have been signed in successfully.",
        });
        navigate("/");
      } else {
        showError(result.error || "Sign in failed. Please try again.");
      }
    } catch (error) {
      console.error("Unexpected error during sign in:", error);
      showError("🌐 An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);

    try {
      const result = await resetPassword(resetEmail);
      
      if (result.success) {
        showSuccess("📧 Password reset email sent! Please check your inbox and spam folder for reset instructions from Supabase.");
        toast({
          title: "Reset Email Sent!",
          description: "Check your email (including spam folder) for password reset instructions.",
        });
        setShowResetForm(false);
        setResetEmail("");
      } else {
        // Provide more helpful error messages
        let errorMessage = result.error || "Password reset failed";
        
        if (errorMessage.includes("rate limit") || errorMessage.includes("too many")) {
          errorMessage = "⏰ Too many reset attempts. Please wait a few minutes before trying again.";
        } else if (errorMessage.includes("not found") || errorMessage.includes("invalid email")) {
          errorMessage = "📧 No account found with this email address. Please check your email or sign up for a new account.";
        } else if (errorMessage.includes("network") || errorMessage.includes("connection")) {
          errorMessage = "🌐 Network error. Please check your internet connection and try again.";
        }
        
        showError(errorMessage);
      }
    } catch (error) {
      console.error("Password reset error:", error);
      showError("🌐 An unexpected error occurred. Please check your internet connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuperAdminSetup = async () => {
    setSetupLoading(true);
    clearMessages();

    try {
      const { data, error } = await supabase.functions.invoke("setup-super-admin");

      if (error) {
        throw error;
      }

      if (data.success) {
        showSuccess("✅ Super admin account created! You can now sign in with edward@shogunaillc.com");
        setShowSetupButton(false);
        toast({
          title: "Setup Complete!",
          description: "Super admin account is ready. You can now sign in.",
        });
      } else {
        showError(data.message || "Setup failed");
      }
    } catch (error: any) {
      console.error("Super admin setup error:", error);
      showError("Setup failed: " + (error.message || "Unknown error"));
    } finally {
      setSetupLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo and Branding */}
        <div className="text-center">
          <Link to="/landing" className="inline-block">
            <img 
              src="/lovable-uploads/91b3768c-9bf7-4a1c-b2be-aea61a3ff3be.png" 
              alt="Asset Guardian Logo" 
              className="h-16 w-16 mx-auto mb-4" 
            />
          </Link>
          <h1 className="text-2xl font-bold text-foreground">Asset Guardian</h1>
          <p className="text-muted-foreground">Access your facilities management platform</p>
        </div>

        {showResetForm ? (
          <Card>
            <CardHeader>
              <CardTitle>Reset Your Password</CardTitle>
              <CardDescription>
                Enter your email address and we'll send you a secure reset link
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div>
                  <Label htmlFor="resetEmail">Email Address</Label>
                  <Input
                    id="resetEmail"
                    type="email"
                    placeholder="Enter the email for your account"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    We'll send reset instructions to this email address
                  </p>
                </div>

                {(error || successMessage) && (
                  <Alert variant={error ? "destructive" : "default"} className={successMessage ? "border-green-200 bg-green-50 text-green-800" : ""}>
                    <AlertDescription>
                      {error || successMessage}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-2">
                  <Button 
                    type="submit" 
                    disabled={loading || !resetEmail.trim() || isRateLimited}
                    className="flex-1"
                  >
                    {loading ? "Sending..." : isRateLimited ? "Please wait..." : "Send Reset Link"}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => {
                      setShowResetForm(false);
                      clearMessages();
                      setResetEmail("");
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="space-y-2 mb-4">
                  <h3 className="text-lg font-semibold">Welcome Back</h3>
                  <p className="text-sm text-muted-foreground">
                    Sign in to your approved account
                  </p>
                </div>
                
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div>
                    <Label htmlFor="loginEmail">Email</Label>
                    <Input
                      id="loginEmail"
                      type="email"
                      placeholder="Enter your email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="loginPassword">Password</Label>
                    <Input
                      id="loginPassword"
                      type="password"
                      placeholder="Enter your password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  {(error || successMessage) && (
                    <Alert variant={error ? "destructive" : "default"} className={successMessage ? "border-green-200 bg-green-50 text-green-800" : ""}>
                      <AlertDescription>{error || successMessage}</AlertDescription>
                    </Alert>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading || !loginEmail.trim() || !loginPassword.trim() || isRateLimited}
                  >
                    {loading ? "Signing in..." : isRateLimited ? "Please wait..." : "Sign In"}
                  </Button>
                  
                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowResetForm(true)}
                      className="text-sm text-primary hover:underline block w-full"
                      disabled={loading}
                    >
                      Forgot your password?
                    </button>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          Don't have an account?
                        </span>
                      </div>
                    </div>
                    <Link to="/register">
                      <Button variant="outline" className="w-full" type="button">
                        Request Access
                      </Button>
                    </Link>
                  </div>
                </form>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Additional Info */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            Need help? Contact your administrator for access.
          </p>
        </div>

        {/* Super Admin Setup - Only show if account doesn't exist */}
        {!checkingSuperAdmin && showSetupButton && (
          <div className="text-center pt-4 border-t border-border">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                System Setup Required
              </p>
              <Button
                onClick={handleSuperAdminSetup}
                disabled={setupLoading}
                variant="outline"
                size="sm"
              >
                {setupLoading ? "Setting up..." : "Setup Super Admin"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;